import { CheckOutlined, CopyOutlined, LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import { Alert, Input, Popconfirm, Select, Skeleton, Tooltip } from "antd";
import {
    useGetConversionApiToken,
    useGetConversionApiTokenForUser,
    useRegenerateConversionApiTokenMutation,
    useRegenerateConversionApiTokenForUserMutation,
} from "api/TenantSlug/User/hooks.ts";
import { useOfferUserList } from "components/Forms/shared/useOfferUserList";
import { FormCol } from "components/UI/Form/FormCol.tsx";
import { FormGrid } from "components/UI/Form/FormGrid.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { TextArea } from "components/UI/TextArea/TextArea.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums.ts";
import { FormId } from "types/models.ts";
import { z } from "zod";

const schema = z.object({
    form_structure: z.string(),
    endpoint: z.string(),
    token: z.string(),
    user: z.string().optional(),
});

type OfferApiFormData = z.infer<typeof schema>;

interface OfferApiDetailProps {
    offer: App.Data.OfferData;
    offerId: string;
    showUserSelect?: boolean;
    onEndpointChange?: (endpoint: string) => void;
}

const OfferApiDetailContent: FC<OfferApiDetailProps> = ({
    offer,
    offerId,
    showUserSelect = false,
    onEndpointChange,
}) => {
    const { t } = usePaldockTranslation("forms.offer-api-detail");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const generalFormUtils = useGeneralForm<OfferApiFormData>();
    const { control, setValue, getValues, fieldNames } = generalFormUtils;

    const userUtils = useUser();
    const permissionUtils = usePermission();
    const currentUser = userUtils.getAuthenticatedUser();

    const canViewOwn = permissionUtils.hasPermission(PermissionEnum.OfferApiViewOwn);
    const canViewOthers = permissionUtils.hasPermission(PermissionEnum.OfferApiViewOthers);
    const canRegenerateOwn = permissionUtils.hasPermission(PermissionEnum.OfferApiRegenerateOwnToken);
    const canRegenerateForeign = permissionUtils.hasPermission(PermissionEnum.OfferApiRegenerateForeignToken);
    const showUserSelectGuarded = showUserSelect && canViewOthers;

    const {
        users,
        isLoading: isUsersLoading,
        defaultUserId,
    } = useOfferUserList({
        offer,
        showUserSelectGuarded,
        canViewOwn,
        currentUser,
    });

    const [isRegenerating, setIsRegenerating] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(defaultUserId);
    const isViewingOwn = selectedUserId === currentUser?.id;

    const getConversionApiToken = useGetConversionApiToken({
        enabled: isViewingOwn,
    });

    const getConversionApiTokenForUser = useGetConversionApiTokenForUser(selectedUserId, {
        enabled: !isViewingOwn && !!selectedUserId,
    });

    const activeTokenQuery = isViewingOwn ? getConversionApiToken : getConversionApiTokenForUser;
    const canRegenerateActive = isViewingOwn ? canRegenerateOwn : canRegenerateForeign;

    const regenerateConversionApiToken = useRegenerateConversionApiTokenMutation({
        onSuccess: () => {
            getConversionApiToken.refetch();
            setIsRegenerating(false);
        },
        onError: (errorResponse) => {
            console.error(errorResponse);
            toast.error(t("messages.token_generation_error"));
            setIsRegenerating(false);
        },
    });

    const regenerateConversionApiTokenForUser = useRegenerateConversionApiTokenForUserMutation({
        onSuccess: () => {
            getConversionApiTokenForUser.refetch();
            setIsRegenerating(false);
        },
        onError: (errorResponse) => {
            console.error(errorResponse);
            toast.error(t("messages.token_generation_error"));
            setIsRegenerating(false);
        },
    });

    // Only ever called from the explicit regenerate button click — never automatically.
    const handleRegenerate = () => {
        if (isViewingOwn && canRegenerateOwn) {
            setIsRegenerating(true);
            regenerateConversionApiToken.mutate();
        } else if (selectedUserId && canRegenerateForeign) {
            setIsRegenerating(true);
            regenerateConversionApiTokenForUser.mutate(selectedUserId);
        }
    };

    // Sync selectedUserId with the users list without discarding a valid manual selection, and
    // always mirror the resolved id into the RHF "user" field. Must run even when selectedUserId
    // is already valid (e.g. it resolved synchronously on the very first render for private
    // offers, since useOfferUserList needs no async fetch there) — otherwise setValue("user", …)
    // would never fire at all and the form's "user" value would stay undefined forever, even
    // though selectedUserId (and the loaded token) are already correct.
    useEffect(() => {
        if (!users.length) {
            // Keep the last valid selection during transient empty states while related
            // data is still settling. Otherwise the token field briefly clears and the UI
            // flips from own-token mode to foreign-token mode.
            return;
        }

        const hasCurrentSelection = selectedUserId ? users.some((u) => u.id === selectedUserId) : false;
        const resolvedUserId = hasCurrentSelection
            ? (selectedUserId as string)
            : defaultUserId && users.some((u) => u.id === defaultUserId)
              ? defaultUserId
              : users[0].id;

        if (!hasCurrentSelection) {
            setSelectedUserId(resolvedUserId);
        }
        setValue("user", resolvedUserId);
    }, [users, defaultUserId, selectedUserId, setValue]);

    const handleUserChange = (val: string) => {
        setSelectedUserId(val);
        setValue("user", val);
    };

    const [formStructureId, setFormStructureId] = useState<string | null>(null);
    const hasFormStructures = offer.form_structures && offer.form_structures.length > 0;

    // Helper to set endpoint and notify parent
    const setEndpoint = useCallback(
        (endpoint: string) => {
            setValue("endpoint", endpoint);
            onEndpointChange?.(endpoint);
        },
        [setValue, onEndpointChange]
    );

    // Build endpoint URL
    const buildEndpoint = useCallback(
        (structureId: string) => {
            const tenantSlug = userUtils.getActiveTenant()?.slug_name;
            if (!tenantSlug) {
                console.error("Cannot build endpoint: tenant slug is unavailable");
                return "";
            }
            return `${import.meta.env.VITE_API_URL}/${tenantSlug}/conversions/api?o=${offerId}&structure=${structureId}`;
        },
        [offerId, userUtils]
    );

    // Reflect the loaded token into the form. Token (re)generation is NEVER triggered here —
    // it only happens when the user confirms the regenerate Popconfirm (handleRegenerate).
    // When the selected user has no token yet, the field stays empty until they regenerate.
    useEffect(() => {
        setValue("token", activeTokenQuery.data?.token ?? "");
    }, [activeTokenQuery.data, setValue]);

    // Set initial form structure and endpoint
    useEffect(() => {
        if (!hasFormStructures) {
            setEndpoint(t("messages.select_form_structure"));
            return;
        }

        const structureId = formStructureId || offer.form_structures?.[0]?.id;
        if (structureId) {
            setValue("form_structure", structureId);
            setEndpoint(buildEndpoint(structureId));
        }
    }, [offer.form_structures, formStructureId, hasFormStructures, buildEndpoint, setEndpoint, setValue, t]);

    const handleChangeStructure = (value: string) => {
        setFormStructureId(value);
    };

    const [tokenCopied, setTokenCopied] = useState(false);
    const tokenCopyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => {
            if (tokenCopyTimeoutRef.current) {
                clearTimeout(tokenCopyTimeoutRef.current);
            }
        };
    }, []);

    const copyToken = () => {
        const token = getValues("token");
        if (token) {
            navigator.clipboard.writeText(token).then(() => {
                setTokenCopied(true);
                tokenCopyTimeoutRef.current = setTimeout(() => setTokenCopied(false), 2000);
            });
        }
    };

    const isLoading = activeTokenQuery.isLoading || isUsersLoading;

    if (isLoading) {
        return <FormSkeleton />;
    }

    const topRowCols = showUserSelectGuarded ? 3 : 2;

    return (
        <FormCol>
            {hasFormStructures ? (
                <>
                    <FormGrid cols={topRowCols} lgCols={topRowCols}>
                        <FormItem
                            control={control}
                            name={fieldNames.form_structure.name}
                            label={tFields("form_structure.label")}
                        >
                            <Select
                                options={offer.form_structures?.map((formStructure) => ({
                                    label: formStructure.name,
                                    value: formStructure.id?.toString() ?? formStructure.name,
                                }))}
                                onChange={handleChangeStructure}
                                placeholder={tFields("form_structure.placeholder")}
                            />
                        </FormItem>
                        {showUserSelectGuarded && (
                            <FormItem
                                control={control}
                                name={fieldNames.user?.name || "user"}
                                label={tFields("user.label")}
                            >
                                <Select
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                                    }
                                    placeholder={tFields("user.placeholder")}
                                    options={users.map((u) => ({
                                        label: u.nickname ?? u.email ?? tFields("user.no_name"),
                                        value: u.id,
                                    }))}
                                    onChange={handleUserChange}
                                />
                            </FormItem>
                        )}
                        <FormItem control={control} name={fieldNames.token?.name} label={t("fields.api_token.label")}>
                            <Input
                                readOnly
                                addonAfter={
                                    <div className="flex items-center gap-3">
                                        {canRegenerateActive &&
                                            (isRegenerating ? (
                                                <LoadingOutlined />
                                            ) : (
                                                <Popconfirm
                                                    title={t("actions.regenerate_confirm.title")}
                                                    description={t("actions.regenerate_confirm.description")}
                                                    okText={t("actions.regenerate_confirm.ok")}
                                                    cancelText={t("actions.regenerate_confirm.cancel")}
                                                    okButtonProps={{ danger: true }}
                                                    onConfirm={handleRegenerate}
                                                >
                                                    <Tooltip title={t("actions.regenerate_token")}>
                                                        <ReloadOutlined className="cursor-pointer hover:text-primary" />
                                                    </Tooltip>
                                                </Popconfirm>
                                            ))}
                                        {tokenCopied ? (
                                            <CheckOutlined className="text-green-500" />
                                        ) : (
                                            <CopyOutlined
                                                className="cursor-pointer hover:text-primary"
                                                onClick={copyToken}
                                            />
                                        )}
                                    </div>
                                }
                            />
                        </FormItem>
                    </FormGrid>
                    <FormGrid cols={1}>
                        <FormItem
                            control={control}
                            name={fieldNames.endpoint?.name}
                            label={t("fields.offer_endpoint.label")}
                        >
                            <TextArea name="endpoint" appearance="readonly" />
                        </FormItem>
                    </FormGrid>
                </>
            ) : (
                <Alert type="info" showIcon message={t("messages.no_active_form")} />
            )}
        </FormCol>
    );
};

export const OfferApiDetailForm: FC<OfferApiDetailProps> = ({ offer, offerId, showUserSelect, onEndpointChange }) => {
    const defaultValues: OfferApiFormData = {
        form_structure: "",
        endpoint: "",
        token: "",
    };

    return (
        <GeneralFormProvider<OfferApiFormData>
            key={offerId || "create"}
            schema={schema}
            defaultValues={defaultValues}
            id={FormId.OfferApiDetailForm}
        >
            <OfferApiDetailContent
                offer={offer}
                offerId={offerId}
                showUserSelect={showUserSelect}
                onEndpointChange={onEndpointChange}
            />
        </GeneralFormProvider>
    );
};

const FormSkeleton = () => (
    <div className="flex flex-col">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-[16px]">
            <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            <Skeleton.Input className="!w-full !h-[32px] mb-[16px]" />
            <Skeleton.Input className="!w-full !h-[32px] mb-[16px]" />
            <div className="col-span-1 lg:col-span-2">
                <Skeleton.Input className="!min-w-[60px] !w-[60px] !h-[20px]" />
            </div>
            <div className="col-span-1 lg:col-span-2">
                <Skeleton.Input className="!w-full !h-[100px]" />
            </div>
        </div>
    </div>
);
